import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, FileText, CheckCircle, Pen, DownloadSimple, Eye, PencilSimple, Archive, ArrowCounterClockwise, Trash, UsersThree, UploadSimple } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  listContracts, createContract, updateContract, validateContract, signContract,
  archiveContract, unarchiveContract, downloadContract, previewContractPdf, listCooperators, CONTRACT_STATUSES,
} from '../../services/cmcApi';
import { CmcPageHeader, CmcStatusBadge, CmcEmpty, fmtDateOnly } from '../../components/cmc/CmcCommon';
import { SignaturePad } from '../../components/SignaturePad';
import apiInstance from '../../services/api';
import { formatApiError } from '../../utils/apiError';

const emptySignForm = {
  signed_by: '',
  signature_method: 'manual',
  signature_date: new Date().toISOString().slice(0, 10),
};

/**
 * Composant générique pour les conventions/contrats CMC.
 *  - contractType : "CIPAESSE" | "CIPASCOP" | "APPORT_INDUSTRIE"
 *  - defaultTitle : titre par défaut (Convention cadre C.I.P.A.E.S.S.E, …)
 *  - subtitle : sous-titre affiché en tête
 *  - allowCollective : autorise la sélection de plusieurs coopérateurs + quote-parts
 *  - showMissionFields : affiche les champs "objet de mission" + "périmètre"
 */
export default function ConventionsPage({
  contractType = 'CIPAESSE',
  defaultTitle = 'Convention cadre C.I.P.A.E.S.S.E',
  headerTitle = 'Convention cadre C.I.P.A.E.S.S.E',
  subtitle = 'Conventions individuelles signées entre l\'organisme et chaque coopérateur',
  allowCollective = false,
  showMissionFields = false,
  testIdRoot = 'cipaesse',
}) {
  const [items, setItems] = useState([]);
  const [cooperators, setCooperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null | contract
  const [viewDialog, setViewDialog] = useState(null); // contract to view
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [signDialog, setSignDialog] = useState(null); // contract to sign
  const [signForm, setSignForm] = useState(emptySignForm);
  const [signatureImage, setSignatureImage] = useState(null); // base64 data URL
  const [signMode, setSignMode] = useState('draw'); // draw | upload | none
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const emptyForm = {
    cooperator_id: '',
    contract_type: contractType,
    title: defaultTitle,
    version: '1.0',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '',
    mission_object: '',
    mission_scope: '',
    is_collective: false,
    additional_cooperator_ids: [],
    industry_contributions: [],
    status: 'draft',
  };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cts, coops] = await Promise.all([
        listContracts({ limit: 200, contract_type: contractType, archived: showArchived }),
        listCooperators({ limit: 200 }),
      ]);
      setItems(cts.data.items || []);
      setCooperators(coops.data.items || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [contractType, showArchived]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load PDF preview when view dialog opens
  useEffect(() => {
    let objectUrl = null;
    async function loadPreview() {
      if (!viewDialog) { setPreviewUrl(null); return; }
      setPreviewLoading(true);
      try {
        const res = await previewContractPdf(viewDialog.id);
        const blob = new Blob([res.data], { type: 'application/pdf' });
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      } catch (e) {
        setPreviewUrl(null);
      } finally {
        setPreviewLoading(false);
      }
    }
    loadPreview();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [viewDialog]);

  const coopMap = Object.fromEntries(cooperators.map((c) => [c.id, c]));
  const coopName = (id) => {
    const c = coopMap[id];
    if (!c) return id || '-';
    return c.type === 'personne_morale' ? c.legal_name : `${c.first_name || ''} ${c.last_name || ''}`.trim();
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      cooperator_id: c.cooperator_id || '',
      contract_type: c.contract_type || contractType,
      title: c.title || defaultTitle,
      version: c.version || '1.0',
      start_date: c.start_date || '',
      end_date: c.end_date || '',
      mission_object: c.mission_object || '',
      mission_scope: c.mission_scope || '',
      is_collective: !!c.is_collective,
      additional_cooperator_ids: c.additional_cooperator_ids || [],
      industry_contributions: c.industry_contributions || [],
      status: c.status || 'draft',
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.cooperator_id) { toast.error('Sélectionnez un coopérateur principal'); return; }
    setSaving(true);
    try {
      // Enrich industry_contributions with cooperator_name for docx
      const payload = { ...form };
      if (allowCollective && payload.industry_contributions?.length) {
        payload.industry_contributions = payload.industry_contributions.map((r) => ({
          ...r,
          cooperator_name: coopName(r.cooperator_id),
        }));
      }
      if (editing) {
        await updateContract(editing.id, payload);
        toast.success('Convention mise à jour');
      } else {
        await createContract(payload);
        toast.success('Convention créée');
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditing(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onValidate = async (c) => {
    try {
      await validateContract(c.id);
      toast.success('Convention validée');
      fetchData();
    } catch { toast.error('Erreur de validation'); }
  };

  const onSign = async () => {
    if (!signForm.signed_by) { toast.error('Indiquez le signataire'); return; }
    setSaving(true);
    try {
      const payload = { ...signForm };
      if (signatureImage) {
        payload.signature_image = signatureImage;
        // If a graphic signature is provided, ensure method is electronic (not manual)
        if (payload.signature_method === 'manual') payload.signature_method = 'electronic';
      }
      await signContract(signDialog.id, payload);
      toast.success('Convention signée');
      setSignDialog(null);
      setSignForm(emptySignForm);
      setSignatureImage(null);
      setSignMode('draw');
      fetchData();
    } catch { toast.error('Erreur de signature'); }
    finally { setSaving(false); }
  };

  const onPngUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Format non supporté (PNG ou JPG uniquement)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setSignatureImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const onDownload = async (c) => {
    try {
      const res = await downloadContract(c.id);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${c.contract_reference || 'convention'}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('DOCX téléchargé (enregistré dans la GED)');
    } catch { toast.error('Erreur téléchargement'); }
  };

  const onArchive = async (c) => {
    try {
      if (c.archived) {
        await unarchiveContract(c.id);
        toast.success('Convention désarchivée');
      } else {
        await archiveContract(c.id);
        toast.success('Convention archivée');
      }
      fetchData();
    } catch { toast.error('Erreur archivage'); }
  };

  const addIndustryRow = () => {
    setForm((f) => ({
      ...f,
      industry_contributions: [...(f.industry_contributions || []), { cooperator_id: '', quote_part: '', apport: '' }],
    }));
  };

  const updateIndustryRow = (idx, key, value) => {
    setForm((f) => {
      const rows = [...(f.industry_contributions || [])];
      rows[idx] = { ...rows[idx], [key]: value };
      return { ...f, industry_contributions: rows };
    });
  };

  const removeIndustryRow = (idx) => {
    setForm((f) => ({
      ...f,
      industry_contributions: (f.industry_contributions || []).filter((_, i) => i !== idx),
    }));
  };

  const toggleAdditionalCoop = (coopId) => {
    setForm((f) => {
      const list = f.additional_cooperator_ids || [];
      return { ...f, additional_cooperator_ids: list.includes(coopId) ? list.filter((x) => x !== coopId) : [...list, coopId] };
    });
  };

  return (
    <div data-testid={`${testIdRoot}-page`}>
      <CmcPageHeader
        title={headerTitle}
        subtitle={subtitle}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant={showArchived ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowArchived(!showArchived)}
              className={showArchived ? 'bg-[#5E6D66]' : ''}
              data-testid={`${testIdRoot}-toggle-archived`}
            >
              <Archive size={14} className="mr-1.5" />
              {showArchived ? 'Actives' : 'Archives'}
            </Button>
            <Button onClick={openCreate} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid={`${testIdRoot}-create-btn`}>
              <Plus size={16} className="mr-2" /> Nouvelle convention
            </Button>
          </div>
        }
      />

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
          ) : items.length === 0 ? (
            <CmcEmpty icon={FileText} message="Aucune convention enregistrée" hint="Cliquez sur « Nouvelle convention » pour commencer." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F8F5] text-[11px] uppercase tracking-wide text-[#5E6D66]">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Référence</th>
                    <th className="text-left px-4 py-2.5 font-medium">Coopérateur(s)</th>
                    <th className="text-left px-4 py-2.5 font-medium">Période</th>
                    <th className="text-left px-4 py-2.5 font-medium">Statut</th>
                    <th className="text-left px-4 py-2.5 font-medium">Créé</th>
                    <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0EFEB]">
                  {items.map((c) => {
                    const extraCoops = (c.additional_cooperator_ids || []).length;
                    return (
                      <tr key={c.id} className="hover:bg-[#FAFAF8]" data-testid={`${testIdRoot}-row-${c.id}`}>
                        <td className="px-4 py-3 font-mono text-xs">{c.contract_reference}</td>
                        <td className="px-4 py-3 text-[#1A2421]">
                          <div className="flex items-center gap-1.5">
                            <span>{coopName(c.cooperator_id)}</span>
                            {c.is_collective && extraCoops > 0 && (
                              <Badge variant="outline" className="text-[9px]">
                                <UsersThree size={9} className="mr-0.5" /> +{extraCoops}
                              </Badge>
                            )}
                            {c.archived && <Badge variant="secondary" className="text-[9px] bg-[#F0EFEB]">Archivée</Badge>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#5E6D66]">
                          {fmtDateOnly(c.start_date)} {c.end_date ? `→ ${fmtDateOnly(c.end_date)}` : ''}
                        </td>
                        <td className="px-4 py-3">
                          <CmcStatusBadge status={c.status} label={CONTRACT_STATUSES.find((s) => s.value === c.status)?.label} />
                        </td>
                        <td className="px-4 py-3 text-xs text-[#5E6D66]">{fmtDateOnly(c.created_at)}</td>
                        <td className="px-4 py-3 text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => setViewDialog(c)} data-testid={`${testIdRoot}-view-${c.id}`} title="Visualiser">
                            <Eye size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => onDownload(c)} data-testid={`${testIdRoot}-download-${c.id}`} title="Télécharger DOCX">
                            <DownloadSimple size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`${testIdRoot}-edit-${c.id}`} title="Modifier">
                            <PencilSimple size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => onArchive(c)} data-testid={`${testIdRoot}-archive-${c.id}`} title={c.archived ? 'Désarchiver' : 'Archiver'}>
                            {c.archived ? <ArrowCounterClockwise size={14} /> : <Archive size={14} />}
                          </Button>
                          {c.status === 'draft' && (
                            <Button size="sm" variant="outline" onClick={() => onValidate(c)} data-testid={`${testIdRoot}-validate-${c.id}`}>
                              <CheckCircle size={14} className="mr-1" /> Valider
                            </Button>
                          )}
                          {(c.status === 'validated' || c.status === 'draft') && (
                            <Button size="sm" className="bg-[#2D5A4C] hover:bg-[#1E4136]" onClick={() => { setSignForm(emptySignForm); setSignDialog(c); }} data-testid={`${testIdRoot}-sign-${c.id}`}>
                              <Pen size={14} className="mr-1" /> Signer
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={`${testIdRoot}-dialog`}>
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier la convention' : `Nouvelle ${headerTitle.toLowerCase()}`}</DialogTitle>
            <DialogDescription>Convention {allowCollective ? 'individuelle ou collective' : 'individuelle'} entre l'organisme et le(s) coopérateur(s).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titre</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid={`${testIdRoot}-form-title`} />
            </div>
            <div>
              <Label>Coopérateur principal *</Label>
              <Select value={form.cooperator_id} onValueChange={(v) => setForm({ ...form, cooperator_id: v })}>
                <SelectTrigger data-testid={`${testIdRoot}-form-cooperator`}><SelectValue placeholder="Choisir un coopérateur" /></SelectTrigger>
                <SelectContent>
                  {cooperators.filter((c) => c.type === 'personne_physique' || !allowCollective).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.type === 'personne_morale' ? c.legal_name : `${c.first_name || ''} ${c.last_name || ''}`.trim()} ({c.numero_associe})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date début</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid={`${testIdRoot}-form-start`} />
              </div>
              <div>
                <Label>Date fin</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid={`${testIdRoot}-form-end`} />
              </div>
            </div>

            {showMissionFields && (
              <>
                <div>
                  <Label>Objet de la mission coopérative</Label>
                  <Textarea rows={2} value={form.mission_object} onChange={(e) => setForm({ ...form, mission_object: e.target.value })} data-testid={`${testIdRoot}-form-mission-object`} />
                </div>
                <div>
                  <Label>Périmètre</Label>
                  <Textarea rows={2} value={form.mission_scope} onChange={(e) => setForm({ ...form, mission_scope: e.target.value })} data-testid={`${testIdRoot}-form-mission-scope`} />
                </div>
              </>
            )}

            {allowCollective && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">Convention collective</Label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_collective}
                      onChange={(e) => setForm({ ...form, is_collective: e.target.checked })}
                      data-testid={`${testIdRoot}-form-is-collective`}
                    />
                    Convention collective (multi-coopérateurs)
                  </label>
                </div>

                {form.is_collective && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Coopérateurs personnes physiques associés</Label>
                      <div className="max-h-40 overflow-y-auto border border-[#E8E6E1] rounded p-2 space-y-1 mt-1" data-testid={`${testIdRoot}-form-additional-coops`}>
                        {cooperators.filter((c) => c.type === 'personne_physique' && c.id !== form.cooperator_id).length === 0 && (
                          <p className="text-xs text-[#5E6D66] py-2 text-center">Aucun autre coopérateur personne physique disponible</p>
                        )}
                        {cooperators.filter((c) => c.type === 'personne_physique' && c.id !== form.cooperator_id).map((c) => (
                          <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-[#F9F8F5] p-1 rounded">
                            <input
                              type="checkbox"
                              checked={(form.additional_cooperator_ids || []).includes(c.id)}
                              onChange={() => toggleAdditionalCoop(c.id)}
                            />
                            {`${c.first_name || ''} ${c.last_name || ''}`.trim()} ({c.numero_associe})
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs">Apports en industrie / quote-parts</Label>
                        <Button size="sm" variant="outline" onClick={addIndustryRow} data-testid={`${testIdRoot}-form-add-industry`}>
                          <Plus size={12} className="mr-1" /> Ajouter une ligne
                        </Button>
                      </div>
                      {(form.industry_contributions || []).length === 0 ? (
                        <p className="text-xs text-[#5E6D66] py-1">Aucune quote-part définie.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(form.industry_contributions || []).map((row, idx) => (
                            <div key={idx} className="grid grid-cols-[1fr_100px_140px_36px] gap-2 items-center">
                              <Select value={row.cooperator_id} onValueChange={(v) => updateIndustryRow(idx, 'cooperator_id', v)}>
                                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Coopérateur" /></SelectTrigger>
                                <SelectContent>
                                  {cooperators.filter((c) => c.type === 'personne_physique').map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                      {`${c.first_name || ''} ${c.last_name || ''}`.trim()}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                placeholder="Quote-part"
                                className="h-8 text-xs"
                                value={row.quote_part}
                                onChange={(e) => updateIndustryRow(idx, 'quote_part', e.target.value)}
                              />
                              <Input
                                placeholder="Apport (description)"
                                className="h-8 text-xs"
                                value={row.apport}
                                onChange={(e) => updateIndustryRow(idx, 'apport', e.target.value)}
                              />
                              <Button size="sm" variant="ghost" onClick={() => removeIndustryRow(idx)} className="h-8 w-8 p-0 text-red-500">
                                <Trash size={12} />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); }} data-testid={`${testIdRoot}-cancel-btn`}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid={`${testIdRoot}-save-btn`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Enregistrer' : 'Créer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewDialog} onOpenChange={(o) => !o && setViewDialog(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid={`${testIdRoot}-view-dialog`}>
          <DialogHeader>
            <DialogTitle>{viewDialog?.title || headerTitle}</DialogTitle>
            <DialogDescription>Réf. {viewDialog?.contract_reference}</DialogDescription>
          </DialogHeader>
          {viewDialog && (
            <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 flex-1 overflow-hidden">
              {/* Left: metadata */}
              <div className="space-y-3 text-sm overflow-y-auto pr-2">
                <div>
                  <span className="text-xs text-[#5E6D66]">Coopérateur principal</span>
                  <p className="font-medium">{coopName(viewDialog.cooperator_id)}</p>
                </div>
                <div>
                  <span className="text-xs text-[#5E6D66]">Statut</span>
                  <p><CmcStatusBadge status={viewDialog.status} label={CONTRACT_STATUSES.find((s) => s.value === viewDialog.status)?.label} /></p>
                </div>
                <div>
                  <span className="text-xs text-[#5E6D66]">Date début</span>
                  <p>{fmtDateOnly(viewDialog.start_date)}</p>
                </div>
                <div>
                  <span className="text-xs text-[#5E6D66]">Date fin</span>
                  <p>{fmtDateOnly(viewDialog.end_date) || '—'}</p>
                </div>
                {viewDialog.mission_object && (
                  <div>
                    <span className="text-xs text-[#5E6D66]">Objet de la mission</span>
                    <p className="whitespace-pre-wrap text-xs">{viewDialog.mission_object}</p>
                  </div>
                )}
                {viewDialog.mission_scope && (
                  <div>
                    <span className="text-xs text-[#5E6D66]">Périmètre</span>
                    <p className="whitespace-pre-wrap text-xs">{viewDialog.mission_scope}</p>
                  </div>
                )}
                {viewDialog.is_collective && (viewDialog.additional_cooperator_ids || []).length > 0 && (
                  <div>
                    <span className="text-xs text-[#5E6D66]">Coopérateurs associés</span>
                    <ul className="list-disc pl-5 text-xs">
                      {(viewDialog.additional_cooperator_ids || []).map((id) => (
                        <li key={id}>{coopName(id)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              {(viewDialog.industry_contributions || []).length > 0 && (
                <div>
                  <span className="text-xs text-[#5E6D66]">Apports / quote-parts</span>
                  <table className="w-full text-xs mt-1 border border-[#E8E6E1]">
                    <thead className="bg-[#F9F8F5]">
                      <tr>
                        <th className="text-left px-2 py-1">Coopérateur</th>
                        <th className="text-left px-2 py-1">Quote-part</th>
                        <th className="text-left px-2 py-1">Apport</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewDialog.industry_contributions.map((row, idx) => (
                        <tr key={idx} className="border-t border-[#F0EFEB]">
                          <td className="px-2 py-1">{coopName(row.cooperator_id)}</td>
                          <td className="px-2 py-1">{row.quote_part || '—'}</td>
                          <td className="px-2 py-1">{row.apport || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(viewDialog.signatures || []).length > 0 && (
                <div>
                  <span className="text-xs text-[#5E6D66] font-semibold">Timeline des signatures</span>
                  <div className="mt-2 space-y-2" data-testid={`${testIdRoot}-signatures-timeline`}>
                    {viewDialog.signatures.map((s, idx) => (
                      <div key={idx} className="relative pl-4 pb-2 border-l-2 border-[#2D5A4C]">
                        <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-[#2D5A4C]" />
                        <div className="text-[11px]">
                          <p className="font-semibold text-[#1A2421]">{s.signed_by}</p>
                          <p className="text-[#5E6D66]">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium mr-1 ${s.signed_via === 'portal_cooperateur' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                              {s.signed_via === 'portal_cooperateur' ? 'Portail' : s.signature_method}
                            </span>
                            {fmtDateOnly(s.signature_date)}
                          </p>
                          {s.signature_image && (
                            <img src={s.signature_image} alt="Signature" className="mt-1 max-h-[50px] border border-[#E8E6E1] rounded bg-white" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
              {/* Right: PDF preview */}
              <div className="flex flex-col border border-[#E8E6E1] rounded-lg overflow-hidden bg-[#F9F8F5]" data-testid={`${testIdRoot}-view-pdf-container`}>
                {previewLoading ? (
                  <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" />
                  </div>
                ) : previewUrl ? (
                  <iframe
                    src={previewUrl}
                    title="Aperçu de la convention"
                    className="w-full h-full min-h-[500px]"
                    data-testid={`${testIdRoot}-view-pdf-iframe`}
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center text-xs text-[#5E6D66] p-4 text-center">
                    Aperçu indisponible
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => onDownload(viewDialog)} data-testid={`${testIdRoot}-view-download`}>
              <DownloadSimple size={14} className="mr-1" /> Télécharger DOCX
            </Button>
            <Button onClick={() => setViewDialog(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign Dialog */}
      <Dialog open={!!signDialog} onOpenChange={(o) => { if (!o) { setSignDialog(null); setSignatureImage(null); setSignMode('draw'); } }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid={`${testIdRoot}-sign-dialog`}>
          <DialogHeader>
            <DialogTitle>Signer la convention</DialogTitle>
            <DialogDescription>Réf. {signDialog?.contract_reference}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Signataire (nom complet) *</Label>
              <Input value={signForm.signed_by} onChange={(e) => setSignForm({ ...signForm, signed_by: e.target.value })} data-testid={`${testIdRoot}-sign-signed-by`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Méthode de signature</Label>
                <Select value={signForm.signature_method} onValueChange={(v) => setSignForm({ ...signForm, signature_method: v })}>
                  <SelectTrigger data-testid={`${testIdRoot}-sign-method`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manuscrite</SelectItem>
                    <SelectItem value="electronic">Électronique</SelectItem>
                    <SelectItem value="imported">Importée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date de signature</Label>
                <Input type="date" value={signForm.signature_date} onChange={(e) => setSignForm({ ...signForm, signature_date: e.target.value })} data-testid={`${testIdRoot}-sign-date`} />
              </div>
            </div>

            {/* Signature graphique */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Signature graphique</Label>
                <div className="flex gap-1">
                  <Button size="sm" variant={signMode === 'draw' ? 'default' : 'outline'} onClick={() => { setSignMode('draw'); setSignatureImage(null); }} className={signMode === 'draw' ? 'bg-[#2D5A4C] hover:bg-[#1E4136]' : ''} data-testid={`${testIdRoot}-sign-mode-draw`}>
                    <Pen size={12} className="mr-1" /> Tracer
                  </Button>
                  <Button size="sm" variant={signMode === 'upload' ? 'default' : 'outline'} onClick={() => { setSignMode('upload'); setSignatureImage(null); }} className={signMode === 'upload' ? 'bg-[#2D5A4C] hover:bg-[#1E4136]' : ''} data-testid={`${testIdRoot}-sign-mode-upload`}>
                    <UploadSimple size={12} className="mr-1" /> PNG
                  </Button>
                  <Button size="sm" variant={signMode === 'none' ? 'default' : 'outline'} onClick={() => { setSignMode('none'); setSignatureImage(null); }} className={signMode === 'none' ? 'bg-[#2D5A4C] hover:bg-[#1E4136]' : ''} data-testid={`${testIdRoot}-sign-mode-none`}>
                    Aucune
                  </Button>
                </div>
              </div>

              {signMode === 'draw' && !signatureImage && (
                <SignaturePad
                  onSave={(dataUrl) => { setSignatureImage(dataUrl); toast.success('Signature capturée'); }}
                  width={500}
                  height={160}
                />
              )}
              {signMode === 'upload' && !signatureImage && (
                <div className="border-2 border-dashed border-[#E8E6E1] rounded-lg p-6 text-center">
                  <input type="file" accept="image/png,image/jpeg" onChange={onPngUpload} className="hidden" id={`${testIdRoot}-sign-upload-input`} data-testid={`${testIdRoot}-sign-upload-input`} />
                  <label htmlFor={`${testIdRoot}-sign-upload-input`} className="cursor-pointer">
                    <UploadSimple size={28} className="mx-auto text-[#5E6D66] mb-2" />
                    <p className="text-xs text-[#5E6D66]">Cliquez pour importer une signature PNG/JPG</p>
                  </label>
                </div>
              )}
              {signatureImage && (
                <div className="space-y-2" data-testid={`${testIdRoot}-sign-preview`}>
                  <div className="border border-[#E8E6E1] rounded-lg p-2 bg-white flex items-center justify-center">
                    <img src={signatureImage} alt="Signature" className="max-h-[120px] max-w-full" />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setSignatureImage(null)} data-testid={`${testIdRoot}-sign-clear`}>
                    <Trash size={12} className="mr-1" /> Effacer la signature
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSignDialog(null); setSignatureImage(null); setSignMode('draw'); }} data-testid={`${testIdRoot}-sign-cancel`}>Annuler</Button>
            <Button onClick={onSign} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid={`${testIdRoot}-sign-confirm`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmer la signature'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

